'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

import SiteTopBanner from '../bird-landing/top-banner';
import FaqList from './faq-list';
import FooterCtaForm from './footer-cta-form';
import HeroForm from './hero-form';
import MidCtaForm from './mid-cta-form';
import ScrollFadeSection from './scroll-fade-section';
import './welcome-page.css';

const INSIDE_CARDS: { num: string; title: string; items?: string[]; desc?: string }[] = [
  {
    num: '01',
    title: 'Why We Love Bird Watching & Feeding',
    items: [
      'The joy and mental health benefits of bird feeding',
      'The real ecological impact of backyard feeders',
      'How to get your family involved',
    ],
  },
  {
    num: '02',
    title: 'Choosing the Right Bird Feeder',
    desc: 'Match feeder style to the birds you want to attract.',
  },
  {
    num: '03',
    title: 'Selecting the Best Bird Food',
    desc: 'Pick seeds and blends that bring in more species.',
  },
  {
    num: '04',
    title: 'Maintaining Your Feeders',
    desc: 'Clean and care for feeders so birds keep coming back.',
  },
];

const SPECIES_DATA = [
  {
    image: '/images/welcome/american-goldfinch.jpg',
    name: 'American Goldfinch',
    tip: 'Use a tube feeder with Nyjer seed. Males turn brilliant yellow in April.',
    tag: 'Finch Feeder',
  },
  {
    image: '/images/welcome/northern-cardinal.jpg',
    name: 'Northern Cardinal',
    tip: 'Loves safflower and black-oil sunflower. Prefers platform or hopper feeders.',
    tag: 'Platform Feeder',
  },
  {
    image: '/images/welcome/ruby-throated-hummingbird.jpg',
    name: 'Ruby-throated Hummingbird',
    tip: 'Arrives late April. Use a 4:1 water-to-sugar solution. Clean weekly.',
    tag: 'Hummingbird Feeder',
  },
  {
    image: '/images/welcome/eastern-bluebirds.jpg',
    name: 'Eastern Bluebird',
    tip: 'Needs a nest box with a 1.5" entrance hole, mounted 4–6 ft on an open post.',
    tag: 'Bird House',
  },
  {
    image: '/images/welcome/downy-woodpecker.jpg',
    name: 'Downy Woodpecker',
    tip: 'Suet cakes are essential in winter. Will also take sunflower chips.',
    tag: 'Suet Feeder',
  },
  {
    image: '/images/welcome/black-capped-chickadee.jpg',
    name: 'Black-capped Chickadee',
    tip: 'One of the friendliest feeder visitors. Prefers sunflower seeds and suet.',
    tag: 'Tube Feeder',
  },
];

const PROOF_DATA = [
  {
    text: "I've been feeding birds for 15 years and this guide still taught me things. The seed chart alone was worth the download. Highly recommend to any birding enthusiast.",
    name: 'Margaret T.',
    sub: 'Verified Buyer · Ohio',
  },
  {
    text: 'Set up my first feeder last spring following this guide. Within two weeks I had 8 different species visiting. The placement tips made all the difference.',
    name: 'Robert K.',
    sub: 'Verified Buyer · Pennsylvania',
  },
  {
    text: "Finally understand why my goldfinches disappeared every August — they molt! The seasonal guide answered so many questions I'd had for years. Beautiful resource.",
    name: 'Susan L.',
    sub: 'Verified Buyer · Virginia',
  },
];

const TRUST_ITEMS = [
  { icon: '', title: 'Instant Delivery', sub: 'In your inbox in 60 seconds' },
  { icon: '', title: 'Zero Spam', sub: 'Unsubscribe anytime' },
  { icon: '', title: '50,000+ Birders', sub: 'Already subscribed' },
  { icon: '', title: '4.9/5 Stars', sub: 'Guide reader rating' },
];

const STATS = [
  { val: '50K+', label: 'Downloads' },
  { val: '15+', label: 'Species covered' },
  { val: '4.9★', label: 'Reader rating' },
];

const PROOF_COUNTS = [
  { val: '50,000+', label: 'Guide Downloads' },
  { val: '220,000+', label: 'Product Reviews' },
  { val: '4.9 / 5', label: 'Average Rating' },
];

const WelcomePage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className='bg-[#F7F4EF] font-sans leading-relaxed text-[#1a2e1b] antialiased'>
      {/* TOP BANNER */}
      <SiteTopBanner />

      {/* NAV */}
      <nav
        className={`sticky top-0 z-[100] flex h-16 items-center justify-between border-b border-[#e8e6e2] bg-white px-8 transition-shadow ${
          scrolled ? 'shadow-[0_2px_12px_rgba(0,0,0,0.08)]' : ''
        }`}
      >
        <div className='flex items-center gap-2.5 font-poppins text-[18px] font-extrabold uppercase tracking-[0.04em] text-primary no-underline'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='250'
            height='56'
            fill='none'
            viewBox='0 0 250 56'
            className='hidden h-auto w-[180px] lg_block'
          >
            <path
              fill='#2F6132'
              d='M101.69 18.425h-4.166l-9.25 9.122v-6.534c0-.708-.256-1.323-.766-1.822a2.5 2.5 0 0 0-1.822-.766h-.349v22.11h2.937v-9.993l8.124 8.356c.499.522 1.09.928 1.752 1.218a5.4 5.4 0 0 0 2.078.43h1.915l-11.34-11.501zM107.458 18.425h-2.936v22.11h2.936zM129.544 19.168c-.511-.487-1.114-.743-1.822-.743h-.349v17.13l-10.48-14.867a5.36 5.36 0 0 0-1.915-1.648 5.26 5.26 0 0 0-2.472-.603h-.348v19.556c0 .708.256 1.311.743 1.81.499.5 1.103.743 1.81.743h.349V23.044l10.816 15.39a4.9 4.9 0 0 0 1.776 1.555 5 5 0 0 0 2.309.557h.349V20.979c0-.708-.267-1.323-.766-1.81M145.56 29.254c0 .661.244 1.241.719 1.717.476.476 1.057.72 1.718.72h3.83l-.012.325c-.092 1.207-.487 2.275-1.149 3.157-.673.893-1.555 1.578-2.611 2.054a8.3 8.3 0 0 1-3.424.72c-1.624 0-3.052-.384-4.27-1.138a7.63 7.63 0 0 1-2.809-3.075c-.662-1.289-.987-2.74-.987-4.318 0-1.485.349-2.89 1.033-4.166a8.23 8.23 0 0 1 2.867-3.087c1.219-.767 2.6-1.161 4.12-1.161 1.497 0 2.785.302 3.807.905 1.033.604 1.857 1.358 2.46 2.252a2.83 2.83 0 0 0 1.068.963c.696.36 1.451.453 2.344.163l.337-.117-.116-.336c-.65-1.892-1.822-3.482-3.47-4.735-1.66-1.242-3.784-1.88-6.325-1.88-2.078 0-3.981.51-5.676 1.508a10.86 10.86 0 0 0-4.004 4.097c-.963 1.718-1.45 3.656-1.45 5.745s.464 4.004 1.369 5.698a10.2 10.2 0 0 0 3.888 4.028c1.66.974 3.609 1.474 5.78 1.474 1.88 0 3.586-.372 5.06-1.115 1.474-.742 2.681-1.775 3.563-3.087.882-1.3 1.439-2.797 1.648-4.445.058-.487.093-1.172.093-2.03v-1.184h-9.366v.348zM171.847 32.039c-.302-.766-.812-1.486-1.497-2.136a6.8 6.8 0 0 0-1.961-1.265 13 13 0 0 0-2.38-.696l-2.727-.522c-.963-.186-1.683-.557-2.147-1.091a2.8 2.8 0 0 1-.696-1.904c0-.916.313-1.67.974-2.32a3.77 3.77 0 0 1 2.356-1.126c.395-.035.674-.058.859-.058 1.753 0 3.087.661 4.062 2.019.256.371.592.661.987.87.394.198.824.302 1.288.302.162 0 .383-.023.65-.07l.499-.104-.07-.348c-.174-.882-.545-1.741-1.114-2.565-.568-.836-1.404-1.52-2.46-2.054s-2.356-.801-3.853-.801a8 8 0 0 0-3.494.79c-1.091.521-1.973 1.264-2.634 2.216-.662.952-.998 2.077-.998 3.32 0 1.624.545 2.959 1.613 3.957.534.51 1.149.928 1.822 1.242a9 9 0 0 0 1.961.65l2.855.58c.94.197 1.788.58 2.519 1.149.696.534 1.033 1.288 1.033 2.31 0 1.137-.418 1.984-1.277 2.587-.882.627-1.961.952-3.215.952a4.93 4.93 0 0 1-2.553-.708 5.16 5.16 0 0 1-1.892-1.88 2.93 2.93 0 0 0-1.044-1.056c-.673-.407-1.474-.5-2.147-.29l-.279.08.023.291c.105 1.08.523 2.124 1.242 3.099.708.963 1.66 1.764 2.82 2.367a8.2 8.2 0 0 0 3.819.917c1.392 0 2.669-.267 3.795-.8 1.137-.535 2.042-1.312 2.704-2.31s.986-2.182.986-3.517c.024-.615-.127-1.311-.429-2.077M188.35 40.569h3.238l7.962-16.074 3.876 7.834h-3.667c-.813.023-1.474.244-1.962.661a4.2 4.2 0 0 0-1.068 1.323l-.336.766h8.437l2.728 5.478h3.238l-11.2-22.55zM226.615 29.474c1.172-1.126 1.764-2.634 1.764-4.48 0-1.195-.29-2.31-.859-3.296-.569-.998-1.462-1.799-2.646-2.39-1.172-.581-2.635-.883-4.352-.883h-7.765v22.11h2.937V21.233h4.677c1.799 0 3.098.371 3.876 1.102s1.161 1.637 1.161 2.751-.407 2.043-1.254 2.843c-.835.79-2.124 1.196-3.818 1.196h-.337c-.359 0-.673.127-.917.383a1.28 1.28 0 0 0-.359.882c0 .267.081.51.255.731l5.176 7.068a5.9 5.9 0 0 0 2.02 1.73c.8.405 1.682.614 2.611.614h.696l-6.685-9.157c1.509-.278 2.797-.916 3.819-1.903M248.514 23.822c-.986-1.671-2.379-3.006-4.143-3.958-1.764-.951-3.783-1.439-6.023-1.439h-6.72v22.11h6.72c2.135 0 4.108-.476 5.861-1.405a10.93 10.93 0 0 0 4.224-3.934c1.045-1.683 1.567-3.621 1.567-5.757 0-2.066-.499-3.946-1.486-5.617m-13.95-2.588h3.656c1.764 0 3.331.36 4.642 1.08a7.64 7.64 0 0 1 3.029 2.947c.708 1.254 1.068 2.67 1.068 4.236 0 1.579-.36 3.018-1.091 4.248-.719 1.23-1.752 2.217-3.064 2.913-1.323.708-2.855 1.056-4.572 1.056h-3.691v-16.48zM184.625 30.46l-1.404-2.112-.012.012-2.333-3.528-2.82-4.236a5.2 5.2 0 0 0-1.752-1.52 5.36 5.36 0 0 0-2.554-.65h-.661L181.7 31.26v9.285h.349c.707 0 1.323-.255 1.833-.755.523-.51.778-1.125.778-1.856v-6.256z'
            ></path>
            <path
              fill='#A1C323'
              d='M193.701 15.269s-4.77 2.31-7.3 5.93c-1.614 2.31-2.031 5.397-1.544 7.277.72-.836 2.17-2.577 3.609-4.654 1.463-2.112 2.472-3.621 3.204-4.851.336-.569.65-1.068.65-1.068s.023.197-.093.604c-.116.394-1.254 2.506-3.447 6.011-1.068 1.706-2.611 3.68-3.47 4.747 2.309-.661 4.305-1.66 5.652-3.087 4.224-4.515 2.739-10.91 2.739-10.91'
            ></path>
            <path
              fill='#2F6132'
              d='M69.252 17.404a28.3 28.3 0 0 0-2.507-4.63 27.6 27.6 0 0 0-3.32-4.028 27 27 0 0 0-4.027-3.32 26.3 26.3 0 0 0-4.63-2.506A27 27 0 0 0 44.16.773c-3.68 0-7.242.72-10.596 2.135a28.3 28.3 0 0 0-4.63 2.507 27.6 27.6 0 0 0-4.028 3.32 27 27 0 0 0-3.32 4.027 26.3 26.3 0 0 0-2.506 4.63 27.1 27.1 0 0 0-2.136 10.596c0 3.494.65 6.894 1.939 10.098a83 83 0 0 1-5.42 5.118c-.023.023-.047.035-.07.058a70.536 70.536 0 0 1-5.989 4.62 49 49 0 0 1-2.68 1.74c-1.544.94-2.46 1.392-2.496 1.404-.626.313-2.228.917-2.228.917s.557 1.16 1.95 1.416q.157.035.313.035c.209 0 .395-.024.569-.058a1.1 1.1 0 0 0 .418-.117c.011 0 .023-.011.058-.023.336-.139.615-.313.928-.487.917-.5 2.437-1.393 4.364-2.704v-.012a79 79 0 0 0 5.466-4.132c.163-.139.325-.267.476-.406 3.563.639 6.743.987 9.633 1.033.348.383.72.755 1.09 1.114.407.546.918 1.01 1.521 1.358.348.29.708.58 1.08.847a27 27 0 0 0 4.886 2.913 27 27 0 0 0 5.477 1.857c1.939.43 3.935.65 5.931.65 3.679 0 7.242-.72 10.596-2.135a28.3 28.3 0 0 0 4.63-2.507 27.6 27.6 0 0 0 4.028-3.32 27 27 0 0 0 3.32-4.026 26.3 26.3 0 0 0 2.506-4.631 27.1 27.1 0 0 0 2.136-10.596c0-3.668-.697-7.242-2.124-10.608M20.658 39.722c.105-.105.198-.209.302-.313a1.24 1.24 0 0 0 .383-.395c.58-.615 1.16-1.254 1.73-1.892 7.81-8.797 11.953-13.3 14.762-16.062 2.913-2.855 4.352-3.795 6.22-4.886.813-.476 1.904-.836 3.076-1.033a11.9 11.9 0 0 1 3.737 0c2.275.383 4.236 1.474 5.478 2.333.43.29.824.673 1.172 1.067-.023 0-.046.012-.07.012-.754.116-2.205.476-3.458 1.694-1.694 1.66-2.484 3.97-2.716 7.997-.209 3.679-1.311 6.522-2.193 8.252-1.613 3.18-4.062 5.802-7.277 7.822-1.01.638-2.565 1.543-4.317 2.124a23 23 0 0 1-4.144.905c-1.601.197-3.04.186-4.155-.012a3.4 3.4 0 0 1-1.02-.348c-.221-.186-.43-.371-.65-.569 1.763-.116 3.411-.37 4.908-.754 1.916-.487 3.633-1.195 5.107-2.112 2.449-1.52 4.515-3.435 6.128-5.71 1.3-1.81 2.31-3.853 3.017-6.058 1.207-3.75 1.103-6.697 1.103-6.825a1.19 1.19 0 0 0-.453-.893 1.2 1.2 0 0 0-.963-.256c-.104.012-2.484.43-5.629 1.52-1.845.64-3.574 1.37-5.141 2.194-1.973 1.033-3.69 2.194-5.107 3.459-1.857 1.671-3.47 3.656-4.642 5.745-1.253 2.228-1.961 4.456-2.031 6.464-.012.29.012.58.046.87-2.135-.057-4.48-.278-6.998-.673a63 63 0 0 0 3.795-3.667m5.583 3.575c.058-1.625.661-3.482 1.729-5.374 1.056-1.869 2.483-3.633 4.155-5.13 1.253-1.125 2.797-2.17 4.584-3.11a35 35 0 0 1 4.793-2.043 40 40 0 0 1 3.819-1.114 20.6 20.6 0 0 1-.929 4.526 19.4 19.4 0 0 1-2.68 5.397 18.9 18.9 0 0 1-5.432 5.06c-1.265.79-2.763 1.404-4.434 1.822-1.578.406-3.377.65-5.315.731-.07 0-.128 0-.197.012a2.7 2.7 0 0 1-.093-.777m40.794-5.63a24.9 24.9 0 0 1-5.315 7.893 24.6 24.6 0 0 1-7.892 5.315 24.8 24.8 0 0 1-9.656 1.95 24.8 24.8 0 0 1-10.4-2.275c-.463-.22-.928-.44-1.38-.685.406-.023.835-.07 1.265-.116 1.567-.197 3.203-.545 4.596-1.01 1.996-.66 3.725-1.67 4.84-2.367 3.585-2.251 6.324-5.2 8.146-8.774a22.5 22.5 0 0 0 1.776-4.758c.36-1.451.592-2.948.673-4.457.244-4.155 1.103-5.536 1.985-6.406.79-.766 1.74-.975 2.112-1.033a5 5 0 0 1 1.01-.035c.545.012 1.218.023 1.683-.546.336-.406.394-.812.383-1.09-.012-.29-.116-.72-.534-1.161-.116-.116-.255-.29-.43-.487-.533-.627-1.276-1.498-2.205-2.136-1.45-.998-3.737-2.275-6.453-2.727a14 14 0 0 0-4.514 0c-1.486.243-2.832.696-3.911 1.334-1.962 1.15-3.587 2.206-6.697 5.246-2.855 2.797-7.021 7.335-14.867 16.179-.186.209-.371.417-.557.615A24.6 24.6 0 0 1 19.335 28c0-3.354.662-6.604 1.95-9.656a24.9 24.9 0 0 1 5.315-7.892 24.6 24.6 0 0 1 7.892-5.315 24.8 24.8 0 0 1 9.656-1.95c3.354 0 6.604.661 9.656 1.95a24.9 24.9 0 0 1 7.892 5.315 24.6 24.6 0 0 1 5.316 7.892A24.8 24.8 0 0 1 68.962 28c0 3.343-.639 6.604-1.927 9.668'
            ></path>
            <path
              fill='#2F6132'
              d='M49.197 20.967a1.474 1.474 0 1 0 0-2.948 1.474 1.474 0 0 0 0 2.948'
            ></path>
          </svg>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='120'
            height='20'
            fill='none'
            viewBox='0 0 120 20'
            className='h-auto lg_hidden'
          >
            <path
              fill='#2F6132'
              d='M11.917 3.012H8.88L2.14 9.66V4.898c0-.516-.186-.964-.558-1.328a1.82 1.82 0 0 0-1.328-.558H0v16.112h2.14v-7.282l5.92 6.09c.364.38.795.676 1.277.887.482.203.998.313 1.514.313h1.396l-8.263-8.381zM16.12 3.012h-2.14v16.112h2.14zM32.217 3.553a1.86 1.86 0 0 0-1.328-.541h-.254v12.483L22.998 4.661a3.9 3.9 0 0 0-1.396-1.201 3.8 3.8 0 0 0-1.801-.44h-.254v14.252c0 .516.186.955.541 1.319.364.364.804.541 1.32.541h.253V6.378l7.883 11.215c.338.482.77.863 1.294 1.133s1.091.406 1.683.406h.254V4.872c0-.515-.195-.964-.558-1.319M43.886 10.899c0 .482.177.905.524 1.252.347.346.77.524 1.252.524h2.791l-.008.237c-.068.88-.356 1.657-.838 2.3a4.8 4.8 0 0 1-1.903 1.497 6.05 6.05 0 0 1-2.495.525c-1.184 0-2.224-.28-3.112-.83a5.56 5.56 0 0 1-2.047-2.24c-.482-.94-.719-1.996-.719-3.147 0-1.082.254-2.106.753-3.036a6 6 0 0 1 2.089-2.25c.888-.558 1.895-.846 3.002-.846 1.092 0 2.03.22 2.775.66.752.44 1.353.99 1.793 1.64.203.305.465.542.778.703.507.262 1.057.33 1.708.118l.245-.084-.084-.246c-.474-1.378-1.328-2.537-2.529-3.45-1.21-.905-2.757-1.37-4.61-1.37-1.513 0-2.9.372-4.135 1.099a7.9 7.9 0 0 0-2.918 2.986c-.702 1.251-1.057 2.664-1.057 4.186 0 1.523.338 2.918.998 4.153a7.44 7.44 0 0 0 2.833 2.935c1.21.71 2.63 1.074 4.212 1.074 1.37 0 2.613-.27 3.688-.812 1.074-.541 1.953-1.294 2.596-2.25a7.36 7.36 0 0 0 1.201-3.24c.042-.354.068-.853.068-1.48v-.862H43.91v.254zM63.043 12.927c-.22-.558-.592-1.082-1.091-1.556a4.9 4.9 0 0 0-1.43-.922 9.5 9.5 0 0 0-1.734-.507l-1.987-.38c-.702-.136-1.227-.407-1.565-.796a2.05 2.05 0 0 1-.507-1.387c0-.668.228-1.218.71-1.691a2.74 2.74 0 0 1 1.717-.82c.288-.026.49-.043.626-.043 1.277 0 2.25.482 2.96 1.471.186.271.431.483.719.635q.431.218.939.22c.118 0 .279-.017.474-.051l.363-.076-.05-.254a5 5 0 0 0-.812-1.87c-.415-.608-1.024-1.107-1.794-1.496-.77-.39-1.717-.584-2.808-.584-.896 0-1.75.195-2.545.575a4.84 4.84 0 0 0-1.92 1.616c-.482.693-.728 1.514-.728 2.419 0 1.184.398 2.157 1.176 2.884.39.372.837.677 1.328.905.482.22.964.38 1.43.474l2.08.422a4.44 4.44 0 0 1 1.835.838c.508.389.753.939.753 1.683 0 .829-.305 1.446-.93 1.886-.643.457-1.43.694-2.343.694-.651 0-1.277-.17-1.86-.517a3.76 3.76 0 0 1-1.38-1.37 2.14 2.14 0 0 0-.76-.77 1.95 1.95 0 0 0-1.565-.21l-.203.059.017.21c.076.787.38 1.549.904 2.26.516.701 1.21 1.285 2.056 1.724a6 6 0 0 0 2.782.669c1.015 0 1.946-.195 2.766-.584a4.73 4.73 0 0 0 1.97-1.683c.483-.727.72-1.59.72-2.563.017-.448-.093-.955-.313-1.514M75.07 19.145h2.36l5.802-11.714 2.825 5.709h-2.673c-.592.017-1.074.178-1.43.482-.337.288-.6.61-.777.964l-.246.559h6.15l1.987 3.992h2.36L83.265 2.703zM102.954 11.064c.854-.82 1.286-1.92 1.286-3.265a4.76 4.76 0 0 0-.626-2.402c-.415-.728-1.066-1.311-1.929-1.742-.854-.423-1.92-.643-3.171-.643h-5.658v16.112h2.14V5.059h3.408c1.31 0 2.258.27 2.825.803.566.533.845 1.193.845 2.004 0 .812-.296 1.49-.913 2.073-.609.575-1.548.87-2.783.87h-.245a.9.9 0 0 0-.668.28.93.93 0 0 0-.262.643c0 .194.059.372.186.533l3.772 5.15c.397.533.888.956 1.472 1.26a4.2 4.2 0 0 0 1.903.449h.507l-4.872-6.673c1.1-.203 2.039-.669 2.783-1.388M118.919 6.945A7.7 7.7 0 0 0 115.9 4.06c-1.286-.693-2.757-1.048-4.39-1.048h-4.897v16.112h4.897c1.557 0 2.994-.347 4.271-1.024a7.97 7.97 0 0 0 3.079-2.867c.761-1.226 1.142-2.639 1.142-4.195 0-1.505-.364-2.875-1.083-4.093m-10.166-1.886h2.664c1.286 0 2.428.262 3.383.786a5.56 5.56 0 0 1 2.208 2.148c.516.914.778 1.946.778 3.087 0 1.15-.262 2.2-.795 3.096a5.57 5.57 0 0 1-2.233 2.123c-.964.516-2.081.77-3.332.77h-2.69V5.059zM72.356 11.782l-1.023-1.539-.009.009-1.7-2.572-2.055-3.087a3.8 3.8 0 0 0-1.277-1.108 3.9 3.9 0 0 0-1.86-.473h-.483l6.276 9.354v6.766h.254c.516 0 .964-.186 1.336-.55.38-.372.567-.82.567-1.353v-4.558z'
            ></path>
            <path
              fill='#A1C323'
              d='M78.971.71s-3.476 1.684-5.32 4.323c-1.175 1.683-1.48 3.933-1.125 5.303a42 42 0 0 0 2.63-3.392c1.066-1.539 1.802-2.639 2.335-3.535.245-.414.474-.778.474-.778s.016.144-.068.44c-.085.287-.913 1.827-2.512 4.38-.778 1.244-1.903 2.682-2.529 3.46 1.683-.482 3.138-1.21 4.119-2.25 3.079-3.29 1.996-7.95 1.996-7.95'
            ></path>
          </svg>
        </div>
        <Link
          href='#get-guide'
          className='inline-block rounded-md bg-primary px-6 py-2 font-poppins text-sm font-semibold text-white no-underline transition-all duration-200 hover_bg-[#245027]'
        >
          Get Free Guide ↓
        </Link>
      </nav>

      {/* HERO */}
      <section
        className='relative flex items-center overflow-hidden px-4 py-12 sm_px-6 sm_py-[72px]'
        style={{ background: 'linear-gradient(155deg, #0d2410 0%, #1a3d1c 30%, #2f6132 65%, #3d7540 100%)' }}
      >
        <div
          className='pointer-events-none absolute inset-0'
          style={{
            backgroundImage:
              'radial-gradient(ellipse 800px 600px at 80% 50%, rgba(106,171,110,0.12) 0%, transparent 70%), radial-gradient(ellipse 400px 400px at 10% 80%, rgba(168,213,170,0.08) 0%, transparent 60%)',
          }}
        />
        <div
          className='pointer-events-none absolute inset-0'
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cellipse cx='40' cy='40' rx='3' ry='8' fill='rgba(255,255,255,0.025)' transform='rotate(30 40 40)'/%3E%3C/svg%3E\")",
          }}
        />
        <div className='relative z-[2] mx-auto grid w-full max-w-[1100px] grid-cols-1 items-center gap-16 lg_grid-cols-[1fr_500px]'>
          {/* LEFT: Copy */}
          <div>
            <div className='mb-5 inline-flex animate-welcome-fade-up items-center gap-2 rounded-[2px] border border-[rgba(168,213,170,0.3)] bg-[rgba(168,213,170,0.15)] px-3.5 py-1.5 font-poppins text-[11px] font-bold uppercase tracking-[0.18em] text-[#A8D5AA] [animation-delay:0.1s]'>
              Free Welcome Bundle · Instant Delivery
            </div>
            <h1 className='font-playfair mb-5 animate-welcome-fade-up text-[36px] font-bold leading-[1.1] text-white [animation-delay:0.22s] lg_text-[56px]'>
              Your <em className='italic text-[#A8D5AA]'>Free</em> Bird Feeding Guide &amp; Welcome Bundle
            </h1>
            <p className='mb-8 max-w-[460px] animate-welcome-fade-up text-[17px] leading-[1.75] text-[rgba(255,255,255,0.75)] [animation-delay:0.34s]'>
              Your complete setup &amp; species reference — expert advice on feeders, seeds, and seasonal care for
              backyard birders.
            </p>
            <div className='flex animate-welcome-fade-up gap-8 [animation-delay:0.46s]'>
              {STATS.map(stat => (
                <div key={stat.label}>
                  <div className='font-poppins text-[26px] font-extrabold leading-none text-white'>{stat.val}</div>
                  <div className='mt-1 text-xs text-[rgba(255,255,255,0.5)]'>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: Form */}
          <div className='animate-welcome-fade-up [animation-delay:0.34s]'>
            <div
              id='get-guide'
              className='relative mb-10 overflow-hidden rounded-lg bg-white px-7 py-8 shadow-[0_20px_60px_rgba(0,0,0,0.14)]'
            >
              <div className='absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary to-[#6aab6e]' />
              <HeroForm />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <div className='bg-[#1a3d1c] px-6 py-[18px]'>
        <div className='mx-auto flex max-w-[1100px] flex-wrap items-center justify-center gap-4 lg_gap-10'>
          {TRUST_ITEMS.map(item => (
            <div
              key={item.title}
              className='flex items-center gap-2.5 text-[rgba(255,255,255,0.75)]'
            >
              <span className='text-[18px]'>{item.icon}</span>
              <div className='font-poppins text-xs font-semibold leading-tight'>
                {item.title}
                <br />
                <span className='text-[#A8D5AA]'>{item.sub}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* WHAT'S INSIDE */}
      <section className='bg-white px-6 py-20'>
        <div className='mx-auto max-w-[1100px] px-6 text-center'>
          <p className='mb-2 font-poppins text-[11px] font-bold uppercase tracking-[0.2em] text-primary'>
            What You&apos;ll Learn
          </p>
          <h2 className='font-playfair mb-3 text-[26px] font-bold leading-tight text-[#1a2e1b] lg_text-[36px]'>
            Everything Inside the <em className='italic text-primary'>Free Guide</em>
          </h2>
          <div className='mx-auto my-4 h-[3px] w-12 rounded-sm bg-[#A8D5AA]' />
          <p className='mx-auto max-w-[600px] text-base leading-[1.75] text-[#777]'>
            Four comprehensive chapters covering every aspect of backyard birding — from your first feeder to building a
            year-round wildlife sanctuary.
          </p>
        </div>
        <ScrollFadeSection className='mx-auto mt-12 grid max-w-[1100px] grid-cols-1 gap-5 px-6 md_grid-cols-2'>
          {INSIDE_CARDS.map(card => (
            <div
              key={card.num}
              className='duration-[500ms] relative translate-y-5 overflow-hidden rounded-md border border-[#EDE9E2] bg-[#F7F4EF] px-6 py-7 opacity-0 transition-all ease-in-out hover_-translate-y-[3px] hover_shadow-[0_8px_32px_rgba(0,0,0,0.10)]'
              data-scroll-fade
            >
              <span className='absolute right-4 top-3 font-poppins text-[56px] font-extrabold leading-none text-[rgba(47,97,50,0.06)]'>
                {card.num}
              </span>
              <h3 className='mb-2.5 font-poppins text-[18px] font-bold text-[#1a2e1b]'>{card.title}</h3>
              {card.items ? (
                <ul className='list-none p-0'>
                  {card.items.map((item, idx) => (
                    <li
                      key={idx}
                      className={`flex items-start gap-2 py-[5px] text-base leading-relaxed text-[#444] ${
                        idx < card.items!.length - 1 ? 'border-b border-[#EDE9E2]' : ''
                      }`}
                    >
                      <span className='shrink-0 font-bold leading-relaxed text-primary'>›</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className='text-base leading-[1.75] text-[#444]'>{card.desc}</p>
              )}
            </div>
          ))}
        </ScrollFadeSection>
        <div className='mt-10 text-center'>
          <Link
            href='https://www.kingsyard.com/birding-guide'
            className='inline-flex h-14 items-center justify-center gap-2 rounded-[10px] bg-primary px-10 font-poppins text-[18px] font-semibold text-white no-underline transition-all duration-[250ms] hover_bg-[#245027]'
          >
            View Birding Guide →
          </Link>
        </div>
      </section>

      {/* BIRD SPECIES */}
      <section
        className='relative overflow-hidden px-6 py-[72px]'
        style={{ background: 'linear-gradient(160deg, #0d2410 0%, #1a3d1c 40%, #2f6132 100%)' }}
      >
        <div
          className='pointer-events-none absolute inset-0'
          style={{
            background: 'radial-gradient(ellipse 600px 400px at 80% 50%, rgba(106,171,110,0.1) 0%, transparent 70%)',
          }}
        />
        <div className='relative z-[2] mx-auto max-w-[1100px] px-6 text-center'>
          <p className='mb-2 font-poppins text-[11px] font-bold uppercase tracking-[0.2em] text-[#A8D5AA]'>
            Species Coverage
          </p>
          <h2 className='font-playfair mb-3 text-[26px] font-bold leading-tight text-white lg_text-[36px]'>
            Meet the Birds <em className='italic text-[#A8D5AA]'>Visiting Your Garden</em>
          </h2>
          <div className='mx-auto my-4 h-[3px] w-12 rounded-sm bg-[#A8D5AA]' />
          <p className='mx-auto max-w-[600px] text-base leading-[1.75] text-[rgba(255,255,255,0.6)]'>
            The guide covers the most common North American backyard species — and exactly what each one needs to
            thrive.
          </p>
        </div>
        <ScrollFadeSection className='relative z-[2] mx-auto mt-10 grid max-w-[1100px] grid-cols-1 gap-4 px-6 sm_grid-cols-2 lg_grid-cols-3'>
          {SPECIES_DATA.map(species => (
            <div
              key={species.name}
              className='duration-[500ms] translate-y-5 overflow-hidden rounded-md border border-[rgba(168,213,170,0.2)] bg-[rgba(255,255,255,0.05)] opacity-0 backdrop-blur-[4px] transition-all ease-in-out hover_-translate-y-[2px] hover_bg-[rgba(168,213,170,0.1)]'
              data-scroll-fade
            >
              <img
                src={species.image}
                alt={species.name}
                className='mb-4 h-auto w-full'
              />
              <div className='px-5 pb-[22px]'>
                <div className='mb-1.5 font-poppins text-[18px] font-bold text-white'>{species.name}</div>
                <div className='text-base leading-relaxed text-[rgba(255,255,255,0.6)]'>{species.tip}</div>
                <span className='mt-2.5 inline-block rounded-[2px] border border-[rgba(168,213,170,0.3)] bg-[rgba(168,213,170,0.15)] px-2.5 py-[3px] font-poppins text-[10px] font-semibold tracking-[0.08em] text-[#A8D5AA]'>
                  {species.tag}
                </span>
              </div>
            </div>
          ))}
        </ScrollFadeSection>
        <div className='relative z-[2] mt-10 text-center'>
          <Link
            href='https://www.kingsyard.com/fun-facts'
            className='inline-flex h-14 items-center justify-center gap-2 rounded-[10px] border-2 border-primary bg-white px-[38px] font-poppins text-[18px] font-semibold text-primary no-underline transition-all duration-[250ms] hover_bg-[#F7F4EF]'
          >
            View More Fun Facts
          </Link>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className='bg-white px-6 py-20'>
        <div className='mx-auto max-w-[1100px] px-6 text-center'>
          <p className='mb-2 font-poppins text-[11px] font-bold uppercase tracking-[0.2em] text-primary'>
            What Readers Say
          </p>
          <h2 className='font-playfair mb-3 text-[26px] font-bold leading-tight text-[#1a2e1b] lg_text-[36px]'>
            Loved by <em className='italic text-primary'>Backyard Birders</em>
          </h2>
          <div className='mx-auto my-4 h-[3px] w-12 rounded-sm bg-[#A8D5AA]' />
        </div>
        <ScrollFadeSection className='mx-auto mt-10 grid max-w-[1100px] grid-cols-1 gap-5 px-6 lg_grid-cols-3'>
          {PROOF_DATA.map(proof => (
            <div
              key={proof.name}
              className='duration-[500ms] translate-y-5 rounded-md border border-[#EDE9E2] bg-[#F7F4EF] p-6 opacity-0 transition-all ease-in-out'
              data-scroll-fade
            >
              <div className='mb-2.5 text-[15px] tracking-[2px] text-[#f4a21a]'>★★★★★</div>
              <p className='mb-3.5 text-base italic leading-[1.7] text-[#444]'>&ldquo;{proof.text}&rdquo;</p>
              <div>
                <div className='font-poppins text-base font-bold text-[#1a2e1b]'>{proof.name}</div>
                <div className='text-xs text-[#777]'>{proof.sub}</div>
              </div>
            </div>
          ))}
        </ScrollFadeSection>
        <div className='mx-auto mt-12 flex max-w-[1100px] flex-wrap justify-center gap-6 px-6 sm_gap-10'>
          {PROOF_COUNTS.map(count => (
            <div
              key={count.label}
              className='text-center'
            >
              <div className='font-poppins text-[36px] font-extrabold leading-none text-primary'>{count.val}</div>
              <div className='mt-1 text-base text-[#777]'>{count.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* MID-PAGE CTA */}
      <section className='bg-[#F7F4EF] px-6 py-20 text-center'>
        <div
          className='relative mx-auto max-w-[660px] overflow-hidden rounded-[10px] px-6 py-9 shadow-[0_20px_60px_rgba(0,0,0,0.14)] sm_px-12 sm_py-[52px]'
          style={{ background: 'linear-gradient(145deg, #1a3d1c, #2f6132)' }}
        >
          <div className='absolute -right-[60px] -top-[60px] h-[200px] w-[200px] rounded-full bg-[rgba(168,213,170,0.08)]' />
          <div className='absolute -bottom-10 -left-10 h-[150px] w-[150px] rounded-full bg-[rgba(168,213,170,0.06)]' />
          <p className='relative z-[2] mb-3 font-poppins text-[11px] font-bold uppercase tracking-[0.2em] text-[#A8D5AA]'>
            Free Welcome Bundle · Instant Delivery
          </p>
          <h2 className='font-playfair relative z-[2] mb-3.5 text-[30px] font-bold leading-tight text-white'>
            Everything you need to
            <br />
            <em className='italic text-[#A8D5AA]'>get started today</em>
          </h2>
          <p className='relative z-[2] mx-auto mb-7 max-w-[440px] text-base leading-[1.7] text-[rgba(255,255,255,0.7)]'>
            Join 50,000+ backyard birders. Get your complete Backyard Bird ID Guide, a 10% discount code, and a
            printable Birding Journal — all free, delivered instantly.
          </p>
          <div className='relative z-[2]'>
            <MidCtaForm />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className='bg-white px-6 py-20'>
        <div className='mx-auto max-w-[760px] px-6 text-center'>
          <p className='mb-2 font-poppins text-[11px] font-bold uppercase tracking-[0.2em] text-primary'>
            Common Questions
          </p>
          <h2 className='font-playfair mb-3 text-[26px] font-bold leading-tight text-[#1a2e1b] lg_text-[36px]'>
            Frequently Asked <em className='italic text-primary'>Questions</em>
          </h2>
          <div className='mx-auto my-4 h-[3px] w-12 rounded-sm bg-[#A8D5AA]' />
        </div>
        <FaqList />
      </section>

      {/* FOOTER CTA */}
      <section
        className='relative overflow-hidden px-6 py-20 text-center'
        style={{ background: 'linear-gradient(135deg, #0d2410 0%, #1a3d1c 50%, #2f6132 100%)' }}
      >
        <div
          className='absolute -top-20 left-1/2 h-[300px] w-[500px] -translate-x-1/2 rounded-full'
          style={{
            background: 'radial-gradient(ellipse, rgba(106,171,110,0.15) 0%, transparent 70%)',
          }}
        />
        <p className='relative z-[1] mb-2 font-poppins text-[11px] font-bold uppercase tracking-[0.2em] text-[#A8D5AA]'>
          Free Welcome Bundle
        </p>
        <h2 className='font-playfair relative z-[1] mx-auto mb-3 max-w-[500px] text-[26px] font-bold leading-tight text-white lg_text-[36px]'>
          Your <em className='italic text-[#A8D5AA]'>Welcome Bundle</em> is waiting
        </h2>
        <div className='relative z-[1] mx-auto my-4 h-[3px] w-12 rounded-sm bg-[#A8D5AA]' />
        <p className='relative z-[1] mx-auto mb-2 max-w-[600px] text-base leading-[1.75] text-[rgba(255,255,255,0.6)]'>
          Your Welcome Bundle — Backyard Bird ID Guide, 10% discount code &amp; printable Birding Journal. Free.
          Instant.
        </p>
        <div className='relative z-[1]'>
          <FooterCtaForm />
        </div>
        <p className='relative z-[1] mt-2 text-xs text-[rgba(255,255,255,0.35)]'>
          No spam · Unsubscribe anytime · We respect your privacy
        </p>
      </section>

      {/* MINI FOOTER */}
      <footer className='flex flex-wrap items-center justify-between gap-3 bg-[#0d2410] px-6 py-6'>
        <div className='font-poppins text-base font-extrabold uppercase tracking-[0.08em] text-[rgba(255,255,255,0.5)]'>
          © 2026 Kingsyard Inc.
        </div>
        <ul className='flex list-none gap-5'>
          {[
            { label: 'Privacy Policy', href: '/privacy-policy' },
            { label: 'Terms of Use', href: '/terms-privacy' },
            { label: 'Contact Us', href: '/contact-us' },
            { label: 'Shop', href: '/bird-feeders' },
          ].map(link => (
            <li key={link.label}>
              <Link
                href={link.href}
                className='font-poppins text-xs font-medium uppercase tracking-[0.06em] text-[rgba(255,255,255,0.4)] no-underline transition-colors hover_text-[#A8D5AA]'
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </footer>
    </div>
  );
};

export default WelcomePage;
